/**
 * Session tracking utilities for analytics
 * Uses sessionStorage to track user sessions
 */

const SESSION_ID_KEY = "odyssey_session_id";
const SESSION_START_TIME_KEY = "odyssey_session_start_time";

/**
 * Generate a simple UUID v4
 */
function generateUUID(): string {
  return "xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx".replace(/[xy]/g, (c) => {
    const r = (Math.random() * 16) | 0;
    const v = c === "x" ? r : (r & 0x3) | 0x8;
    return v.toString(16);
  });
}

/**
 * Get or create a session ID
 * Session ID persists for the browser session (until tab/window closed)
 */
export function getOrCreateSessionId(): string {
  if (typeof window === "undefined") return "";

  let sessionId = sessionStorage.getItem(SESSION_ID_KEY);
  if (!sessionId) {
    sessionId = generateUUID();
    sessionStorage.setItem(SESSION_ID_KEY, sessionId);
    sessionStorage.setItem(SESSION_START_TIME_KEY, Date.now().toString());
  }
  return sessionId;
}

/**
 * Get session start time
 */
export function getSessionStartTime(): number | null {
  if (typeof window === "undefined") return null;

  const startTime = sessionStorage.getItem(SESSION_START_TIME_KEY);
  return startTime ? parseInt(startTime, 10) : null;
}

/**
 * Calculate session duration in seconds
 */
export function getSessionDuration(): number {
  const startTime = getSessionStartTime();
  if (!startTime) return 0;

  return Math.floor((Date.now() - startTime) / 1000);
}

/**
 * Check if this is a new session
 */
export function isNewSession(): boolean {
  if (typeof window === "undefined") return false;
  return !sessionStorage.getItem(SESSION_ID_KEY);
}

/**
 * Clear session data
 */
export function clearSessionData(): void {
  if (typeof window === "undefined") return;

  sessionStorage.removeItem(SESSION_ID_KEY);
  sessionStorage.removeItem(SESSION_START_TIME_KEY);
}
